import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

/**
 * Module-level in-memory cache for the current user's job list.
 *
 * The professional Dashboard, AppraiserDirectory invite-modal, and the
 * appraiser dashboards all hit `/api/jobs` (or `/api/appraiser/jobs`) the
 * moment the page mounts. Re-fetching from scratch every time the user
 * navigates Dashboard → ViewJob → Dashboard wastes ~200 ms and a backend
 * roundtrip. This context implements stale-while-revalidate:
 *
 *   - First call            → fetch, store, return.
 *   - Subsequent calls      → return cached array IMMEDIATELY (instant render)
 *                             while a background refresh runs.
 *   - On logout / role flip → cache wiped.
 *   - After a mutation      → consumer calls `invalidate()` (or `refresh()`
 *                             to force a re-fetch) so the next read gets
 *                             fresh data.
 *
 * Keys are scoped per role so professionals and appraisers don't poison
 * each other's caches if the same browser is reused.
 */

// Admin pages load their own job lists, so the shared cache is not filled for admins (saves a request per page load).
const ENDPOINT_FOR_ROLE = {
  professional: '/api/jobs',
  appraiser: '/api/appraiser/jobs',
};

const JobsContext = createContext({
  jobs: [],
  loading: true,
  refresh: () => Promise.resolve([]),
  invalidate: () => {},
});

export function JobsProvider({ children }) {
  const { user } = useAuth();
  const role = user?.role;
  const userId = user?.user_id;

  // cacheRef survives renders. Keyed by `${role}:${userId}` so switching
  // accounts on the same browser doesn't leak data between users.
  const cacheRef = useRef({ key: null, data: null });
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const inFlightRef = useRef(null);

  const cacheKey = role && userId ? `${role}:${userId}` : null;

  const fetchAndStore = useCallback(async () => {
    if (!cacheKey || !role || !ENDPOINT_FOR_ROLE[role]) return [];
    // De-dupe concurrent fetches — if a refresh is already in flight,
    // every caller awaits the same promise.
    if (inFlightRef.current) return inFlightRef.current;
    const p = (async () => {
      try {
        const res = await api.get(ENDPOINT_FOR_ROLE[role]);
        const data = Array.isArray(res.data) ? res.data : [];
        cacheRef.current = { key: cacheKey, data };
        setJobs(data);
        return data;
      } catch (e) {
        console.error('Failed to load jobs', e);
        return cacheRef.current.data || [];
      } finally {
        inFlightRef.current = null;
        setLoading(false);
      }
    })();
    inFlightRef.current = p;
    return p;
  }, [cacheKey, role]);

  // Public refresh: force a fresh fetch (e.g. after creating/editing a job).
  const refresh = useCallback(() => fetchAndStore(), [fetchAndStore]);

  // Public invalidate: drop the cache so the next render fetches fresh.
  const invalidate = useCallback(() => {
    cacheRef.current = { key: null, data: null };
    setJobs([]);
    setLoading(true);
  }, []);

  // Whenever the cache key changes (login / logout / role switch) reset and
  // fetch. Stale-while-revalidate: render cached data instantly if the same
  // user just navigated away and back.
  useEffect(() => {
    if (!cacheKey) {
      invalidate();
      return;
    }
    if (cacheRef.current.key === cacheKey && cacheRef.current.data) {
      // Cache hit: instant render, background refresh.
      setJobs(cacheRef.current.data);
      setLoading(false);
      fetchAndStore();
    } else {
      // Cache miss: spinner until first fetch.
      setLoading(true);
      fetchAndStore();
    }
  }, [cacheKey, fetchAndStore, invalidate]);

  return (
    <JobsContext.Provider value={{ jobs, loading, refresh, invalidate }}>
      {children}
    </JobsContext.Provider>
  );
}

export const useJobs = () => useContext(JobsContext);
